/**
 * Trip Replay sound, generated with the Web Audio API (no audio files): an engine hum whose
 * pitch and volume follow the speed, plus short chimes for milestones, stops and over-speed.
 * Create it from a user gesture (click) — browsers block audio started any other way.
 */
const ENGINE_SMOOTH = 0.15;   // seconds — time constant for pitch/volume changes, avoids clicks
const MIN_CUE_GAP_MS = 250;   // at ×120 cues can bunch up; keep at most one per 250 ms

const CUES = {
  milestone: [{ type: 'sine', freq: 880, at: 0, len: 0.16, vol: 0.18 }, { type: 'sine', freq: 1320, at: 0.13, len: 0.2, vol: 0.16 }],
  stop: [{ type: 'sine', freq: 520, at: 0, len: 0.2, vol: 0.14 }],
  over: [{ type: 'square', freq: 1000, at: 0, len: 0.08, vol: 0.06 }, { type: 'square', freq: 1000, at: 0.14, len: 0.08, vol: 0.06 }],
};

export function createReplaySound() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  const ctx = new Ctx();

  const engineGain = ctx.createGain();
  engineGain.gain.value = 0;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 600;
  filter.connect(engineGain).connect(ctx.destination);
  const main = ctx.createOscillator();
  main.type = 'sawtooth';
  const sub = ctx.createOscillator();
  sub.type = 'square';
  const subGain = ctx.createGain();
  subGain.gain.value = 0.5;
  main.connect(filter);
  sub.connect(subGain).connect(filter);
  main.start();
  sub.start();

  let lastCue = 0;

  return {
    /** Engine hum for the current speed (km/h); silent when not active or stationary. */
    setEngine(speed, active) {
      if (ctx.state === 'suspended') ctx.resume();
      const now = ctx.currentTime;
      const pitch = 45 + Math.min(speed, 120) * 1.1;
      main.frequency.setTargetAtTime(pitch, now, ENGINE_SMOOTH);
      sub.frequency.setTargetAtTime(pitch / 2, now, ENGINE_SMOOTH);
      const vol = active && speed >= 1 ? 0.03 + Math.min(speed, 80) / 80 * 0.035 : 0;
      engineGain.gain.setTargetAtTime(vol, now, ENGINE_SMOOTH);
    },

    /** One-shot chime: 'milestone' | 'stop' | 'over'. */
    cue(kind) {
      const notes = CUES[kind];
      const ms = Date.now();
      if (!notes || ms - lastCue < MIN_CUE_GAP_MS) return;
      lastCue = ms;
      if (ctx.state === 'suspended') ctx.resume();
      const t0 = ctx.currentTime;
      notes.forEach(n => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = n.type;
        osc.frequency.value = n.freq;
        g.gain.setValueAtTime(0, t0 + n.at);
        g.gain.linearRampToValueAtTime(n.vol, t0 + n.at + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.at + n.len);
        osc.connect(g).connect(ctx.destination);
        osc.start(t0 + n.at);
        osc.stop(t0 + n.at + n.len + 0.02);
        osc.onended = () => { osc.disconnect(); g.disconnect(); };
      });
    },

    /** Fade the engine out (pause, end of trip, mute). */
    stop() {
      engineGain.gain.setTargetAtTime(0, ctx.currentTime, 0.05);
    },

    dispose() {
      try { main.stop(); sub.stop(); } catch (e) { /* already stopped */ }
      ctx.close();
    },
  };
}

/** Index of the first cue strictly after time t (cues sorted by t). */
export function cueIndexAfter(cues, t) {
  let lo = 0, hi = cues.length;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (cues[mid].t <= t) lo = mid + 1; else hi = mid; }
  return lo;
}
